import useDashboardAccess from "@/hooks/useDashboardAccess";
import { MerchantUserDto } from "@/models/club";
import { clubApi } from "@/services/axios";
import { Alert, Button, Card, Group, PinInput, Stack, Switch, Text, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

// Not in the generated client yet.
type Renter = MerchantUserDto & { isChairRenter?: boolean; payoutIban?: string | null; payoutAccountHolder?: string | null };

const ibanOk = (v: string) => /^IR\d{24}$/.test(v.replace(/[\s-]/g, "").toUpperCase()) || /^\d{24}$/.test(v.replace(/[\s-]/g, ""));
const CODE_MINUTES = 10;
const RESEND_SECONDS = 120;
const fa = (n: number) => n.toLocaleString("fa-IR");
const clock = (seconds: number) => `${fa(Math.floor(seconds / 60))}:${fa(seconds % 60).padStart(2, "۰")}`;

// Seconds left until `until`, ticking every second.
function useCountdown(until: number | null) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!until) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [until]);
  return until ? Math.max(0, Math.ceil((until - now) / 1000)) : 0;
}

// «آرایشگر اجاره‌ای»: works under the salon but keeps separate books; online payments for their work
// are settled by Padix to their own account, and the salon's reports leave them out. Turning it off
// needs the renter's consent: a code texted to them, which they read out to the salon.
export default function ChairRental({ user }: { user?: Renter }) {
  const { isManager } = useDashboardAccess();
  const client = useQueryClient();
  const [on, setOn] = useState(false);
  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState("");
  const [code, setCode] = useState("");
  const [sentAt, setSentAt] = useState<number | null>(null);
  useEffect(() => {
    setOn(!!user?.isChairRenter);
    setIban(user?.payoutIban ?? "");
    setHolder(user?.payoutAccountHolder ?? "");
    setCode("");
    setSentAt(null);
  }, [user]);

  const expiresIn = useCountdown(sentAt ? sentAt + CODE_MINUTES * 60_000 : null);
  const resendIn = sentAt ? Math.max(0, expiresIn - (CODE_MINUTES * 60 - RESEND_SECONDS)) : 0;

  const sendCode = useMutation({
    mutationFn: async () => (await clubApi.instance.post("/api/MerchantUser/ChairRental/ReleaseCode", { merchantUserId: user?.id })).data,
    onSuccess: () => {
      setSentAt(Date.now());
      setCode("");
      notifications.show({ color: "violet", message: "کد تأیید برای آرایشگر پیامک شد." });
    },
  });

  const save = useMutation({
    mutationFn: async () =>
      (await clubApi.instance.put("/api/MerchantUser/ChairRental", {
        merchantUserId: user?.id,
        isChairRenter: on,
        payoutIban: on ? iban : null,
        payoutAccountHolder: on ? holder : null,
        releaseCode: on ? null : code,
      })).data,
    onSuccess: () => {
      notifications.show({ color: "green", message: on ? "آرایشگر اجاره‌ای شد؛ همهٔ سوابق او از حساب سالن جدا شد." : "آرایشگر دوباره عضو عادی تیم شد و سوابقش به حساب سالن برگشت." });
      setSentAt(null);
      void client.invalidateQueries();
    },
  });

  // Only a barber can rent a chair, and only the owner or a manager decides it.
  if (!isManager || !user?.id || user.merchantUserType !== 2) return null;
  const releasing = !!user.isChairRenter && !on;
  const changed = on !== !!user.isChairRenter || (on && (iban !== (user.payoutIban ?? "") || holder !== (user.payoutAccountHolder ?? "")));
  const valid = !on || (ibanOk(iban) && holder.trim().length > 1);
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "آرایشگر";

  return (
    <Card withBorder radius="md" p="md" mt="md">
      <Stack gap="sm">
        <Switch size="md" checked={on} onChange={(e) => setOn(e.currentTarget.checked)} label="آرایشگر اجاره‌ای (حساب جدا)"
          description="صندلی اجاره کرده و درآمدش مال خودش است. همچنان عضو این سالن است و خدماتش را از سالن می‌گیرد." />
        {on && (
          <>
            <Alert color="violet" variant="light">
              <Text size="sm" lh={1.9}>
                همهٔ نوبت‌ها، فاکتورها، بیعانه‌ها و پرداخت‌های آنلاین او، از اول تا حالا، به حساب خودش می‌رود و در گزارش‌ها، داشبورد، تسویه تیم و حقوق سالن نمی‌آید. با خاموش کردن، دوباره به حساب سالن برمی‌گردد. تا وقتی اجاره‌ای است، نقش او قابل تغییر نیست و در آمار تیم سالن نمی‌آید.
                پرداخت‌های آنلاین مشتریان او را پدیکس به همین شبا واریز می‌کند. خدمات او را نمی‌شود با خدمات بقیهٔ تیم در یک فاکتور یا رزرو ثبت کرد.
              </Text>
            </Alert>
            <TextInput label="شماره شبا" withAsterisk placeholder="IR000000000000000000000000" value={iban} onChange={(e) => setIban(e.currentTarget.value)}
              styles={{ input: { direction: "ltr", textAlign: "left" } }} error={iban && !ibanOk(iban) ? "شبا باید IR و ۲۴ رقم باشد" : undefined} />
            <TextInput label="نام صاحب حساب" withAsterisk value={holder} onChange={(e) => setHolder(e.currentTarget.value)} />
          </>
        )}

        {releasing ? (
          <Stack gap="sm">
            <Alert color="orange" variant="light">
              <Text size="sm" lh={1.9}>
                با خاموش کردن، حساب‌وکتاب {name} از اول به حساب سالن برمی‌گردد. برای این کار رضایت خودش لازم است:
                یک کد به موبایل او پیامک می‌شود و او کد را برای شما می‌خواند. کد {fa(CODE_MINUTES)} دقیقه اعتبار دارد.
              </Text>
            </Alert>
            {!sentAt || expiresIn === 0 ? (
              <div><Button type="button" color="orange" variant="light" loading={sendCode.isLoading} onClick={() => sendCode.mutate()}>{sentAt ? "ارسال دوبارهٔ کد" : "ارسال کد به آرایشگر"}</Button></div>
            ) : (
              <>
                <Text size="sm">کد تأییدی که برای {name} پیامک شد را وارد کنید:</Text>
                <PinInput length={5} type="number" oneTimeCode value={code} onChange={setCode} aria-label="کد تأیید آرایشگر" style={{ direction: "ltr", justifyContent: "flex-start" }} />
                <Group gap="md">
                  <Text size="xs" c="dimmed">اعتبار کد: {clock(expiresIn)}</Text>
                  <Button type="button" size="compact-xs" variant="subtle" disabled={resendIn > 0} loading={sendCode.isLoading} onClick={() => sendCode.mutate()}>
                    {resendIn > 0 ? `ارسال دوباره تا ${clock(resendIn)}` : "ارسال دوبارهٔ کد"}
                  </Button>
                </Group>
                <div><Button type="button" color="red" variant="light" disabled={code.length !== 5} loading={save.isLoading} onClick={() => save.mutate()}>تأیید و خارج کردن از اجاره</Button></div>
              </>
            )}
          </Stack>
        ) : (
          <div><Button type="button" color="violet" variant="light" disabled={!changed || !valid} loading={save.isLoading} onClick={() => save.mutate()}>ذخیرهٔ نوع همکاری</Button></div>
        )}
      </Stack>
    </Card>
  );
}
